import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, mapTo } from "rxjs/operators";

import { AccessControlKuroiPayload } from "@sharedV11/types/access-control-kuroi.model";
import { ApiTokenService } from "./token/api-token.service";

type SyncMode = "I" | "U" | "D";

const JSON_HEADERS = new HttpHeaders({
  "Content-Type": "application/json",
});

@Injectable({
  providedIn: "root",
})
export class AccessControlSynchronizationService {
  constructor(
    private http: HttpClient,
    private tokenService: ApiTokenService
  ) {}

  sync(
    payload: Partial<AccessControlKuroiPayload>,
    response: any,
    mode: SyncMode
  ): Observable<void> {
    const tasks: Observable<void>[] = [];
    const moduloBody = this.buildModuloConfigPayload(payload, response, mode);
    if (moduloBody) {
      tasks.push(
        this.post("Customan/SincronizarModulosConfigKuroi", moduloBody)
      );
    }

    const conexionBody = this.buildConexionPayload(payload, response, mode);
    if (conexionBody) {
      tasks.push(
        this.post("Customan/SincronizarConexiones", conexionBody)
      );
    }

    if (!tasks.length) {
      return of(void 0);
    }

    return forkJoin(tasks).pipe(
      mapTo(void 0),
      catchError((error) => {
        console.warn(
          "[AccessControlSynchronization] Error al sincronizar datos Kuroi",
          error
        );
        return of(void 0);
      })
    );
  }

  private buildModuloConfigPayload(
    payload: Partial<AccessControlKuroiPayload>,
    response: any,
    mode: SyncMode
  ): any {
    const pkId = this.resolvePkId(payload, response);
    if (!pkId) {
      return null;
    }

    if (mode === "D") {
      return { Funcion: "D", pkId };
    }

    return {
      Funcion: mode,
      Modulo: {
        pkId,
        Nombre: payload?.Nombre ?? response?.DatosResult?.Nombre ?? "",
        TipoAcceso:
          payload?.TipoAcceso ?? response?.DatosResult?.TipoAcceso ?? "",
        TipoConexion:
          payload?.TipoConexion ?? response?.DatosResult?.TipoConexion ?? "",
        Clase: payload?.Clase ?? response?.DatosResult?.Clase ?? "",
        CentrosIds:
          payload?.CentrosIds ?? response?.DatosResult?.CentrosIds ?? "",
        CentrosNombres:
          payload?.CentrosNombres ?? response?.DatosResult?.CentrosNombres ?? "",
        ListadoTiposControl:
          (payload as any)?.ListadoTiposControl ??
          (payload as any)?.ListadoTiposcControl ??
          response?.DatosResult?.ListadoTiposControl ??
          response?.DatosResult?.ListadoTiposcControl ??
          [],
      },
    };
  }

  private buildConexionPayload(
    payload: Partial<AccessControlKuroiPayload>,
    response: any,
    mode: SyncMode
  ): any {
    const pkId = this.resolvePkId(payload, response);
    if (!pkId) {
      return null;
    }

    if (mode === "D") {
      return { Funcion: "D", pkId };
    }

    const resultData = response?.DatosResult ?? {};
    const sqlServer =
      payload?.SQLServer ??
      payload?.Conexion?.SQLServer ??
      (resultData as any)?.SQLServer ??
      (resultData?.Conexion as any)?.SQLServer ??
      "";
    const sqlDataBase =
      payload?.SQLDataBase ??
      payload?.Conexion?.SQLDataBase ??
      (resultData as any)?.SQLDataBase ??
      (resultData?.Conexion as any)?.SQLDataBase ??
      "";
    const sqlUser =
      payload?.SQLUser ??
      payload?.Conexion?.SQLUser ??
      (resultData as any)?.SQLUser ??
      (resultData?.Conexion as any)?.SQLUser ??
      "";
    const sqlPwd =
      payload?.SQLPwd ??
      payload?.Conexion?.SQLPwd ??
      payload?.Conexion?.SQLPassword ??
      (resultData as any)?.SQLPwd ??
      (resultData?.Conexion as any)?.SQLPwd ??
      (resultData?.Conexion as any)?.SQLPassword ??
      "";
    return {
      Funcion: mode,
      Conexion: {
        pkId,
        SQLServer: sqlServer,
        SQLDataBase: sqlDataBase,
        SQLUser: sqlUser,
        SQLPassword: sqlPwd,
        NombreProyecto:
          payload?.NombreProyecto ??
          (resultData as any)?.NombreProyecto ??
          "",
        EsSQLCentralizada:
          payload?.EsSQLCentralizada ?? resultData?.EsSQLCentralizada,
      },
    };
  }

  private post(endpoint: string, body: any): Observable<void> {
    const url = this.getApiUrl(endpoint);
    return this.http.post<void>(url, JSON.stringify(body), {
      headers: JSON_HEADERS,
    });
  }

  private getApiUrl(path: string): string {
    const base = this.ensureTrailingSlash(
      (this.tokenService.getConfig("API_URL") as string) ?? ""
    );
    return `${base}${path}`;
  }

  private ensureTrailingSlash(value: string): string {
    if (!value) {
      return "";
    }
    return value.endsWith("/") ? value : `${value}/`;
  }

  private resolvePkId(
    payload: Partial<AccessControlKuroiPayload>,
    response: any
  ): string | undefined {
    return (
      payload?.pkId ??
      response?.DatosResult?.pkId ??
      response?.DatosResult?.PkId ??
      response?.pkId ??
      undefined
    );
  }
}


